import { Component, OnInit, OnDestroy } from '@angular/core';
import { faEdit, faPlus, faSearch, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { NgbOffcanvas, NgbOffcanvasRef } from '@ng-bootstrap/ng-bootstrap';
import { StorageKeys } from 'src/app/share/enums/storage.enum';
import { ExpenseService } from 'src/app/share/services/expense/expense.service';
import { StorageService } from 'src/app/share/services/storage-service/storage.service';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
@Component({
  selector: 'app-expense-categories',
  templateUrl: './expense-categories.component.html',
  styleUrls: ['./expense-categories.component.scss'],
  standalone: false
})
export class ExpenseCategoriesComponent implements OnInit, OnDestroy {

  protected faPlus = faPlus;
  protected faEdit = faEdit;
  protected faSpinner = faSpinner;
  protected faSearch = faSearch;

  private readonly searchSubject$ = new Subject<string>();
  private searchSubscribe!: Subscription;

  expanseList: any[] = [];
  isCompanyUser: boolean = false;

  loader: boolean = false;
  errorMessage: string = '';
  endOfResult: boolean = false;
  page: number = 0;
  searchTerm: string = '';

  isEditMode: boolean = false;
  editingId: string = '';
  modalCategoryName: string = '';
  modalIsActive: boolean = true;
  modalLoader: boolean = false;
  modalErrorMessage: string = '';

  private offcanvasRef!: NgbOffcanvasRef;

  getBadgeStyle(exp: any) {
    const bg = exp.color ? exp.color + '66' : '#E0F2FE';
    const text = exp.color || '#0369A1';
    return { 'background-color': bg, 'color': text };
  }

  constructor(
    private readonly offcanvasService: NgbOffcanvas,
    private readonly expenseService: ExpenseService,
    private readonly storageService: StorageService
  ) {}

  ngOnInit(): void {
    this.expanseList = [];
    this.getCategories();
    const userDetailsData = this.storageService.getDataFromLocalStorage(StorageKeys.SELF_DETAILS);
    this.isCompanyUser = userDetailsData?.enrollmentType === 'Company';

    this.searchSubscribe = this.searchSubject$.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe((value: any) => {
      this.searchCategory(value);
    });
  }

  ngOnDestroy(): void {
    if (this.searchSubscribe) this.searchSubscribe.unsubscribe();
  }

  getCategories() {
    this.loader = true;
    const payload: any = { page: this.page };
    if (this.searchTerm) payload.name = this.searchTerm;
    this.expenseService.getExpenseCategory(payload).subscribe({
      next: (res: any) => {
        const data = res.data || [];
        if (this.page === 0) {
          this.expanseList = data;
        } else {
          this.expanseList = [...this.expanseList, ...data];
        }
        this.loader = false;
        if (!data.length) this.endOfResult = true;
      },
      error: (err: any) => {
        this.loader = false;
        this.errorMessage = err?.error?.error?.message || 'Failed to get expense categories';
      }
    });
  }

  changeCatInput(term: any) {
    this.searchSubject$.next(term);
  }

  searchCategory(term: any) {
    this.page = 0;
    this.endOfResult = false;
    this.expanseList = [];
    this.searchTerm = term?.trim() || '';
    this.getCategories();
  }

  onscrollTable() {
    if (this.loader || this.endOfResult) return;
    this.page++;
    this.getCategories();
  }

  private openOffcanvas(content: any) {
    const mainEl = document.querySelector('main') as HTMLElement;
    if (mainEl) {
      mainEl.style.overflow = 'visible';
    }

    this.offcanvasRef = this.offcanvasService.open(content, {
      position: 'end',
      backdrop: 'static',
      keyboard: false,
      panelClass: 'category-offcanvas'
    });

    this.offcanvasRef.result.finally(() => {
      if (mainEl) {
        mainEl.style.overflow = '';
      }
      document.body.classList.remove('offcanvas-open');
    });

    document.body.classList.add('offcanvas-open');
  }

  openAddModal(content: any) {
    this.isEditMode = false;
    this.editingId = '';
    this.modalCategoryName = '';
    this.modalIsActive = true;
    this.modalErrorMessage = '';
    this.modalLoader = false;
    this.openOffcanvas(content);
  }

  editExpense(exp: any, content: any) {
    this.isEditMode = true;
    this.editingId = exp._id || '';
    this.modalCategoryName = exp.name;
    this.modalIsActive = exp.status === 'Active';
    this.modalErrorMessage = '';
    this.modalLoader = false;
    this.openOffcanvas(content);
  }

  onSave() {
    if (!this.modalCategoryName.trim()) return;
    this.modalLoader = true;
    this.modalErrorMessage = '';
    if (this.isEditMode) {
      this.updateCategory();
    } else {
      this.addCategory();
    }
  }

  addCategory() {
    const payload = {
      name: this.modalCategoryName.trim().toUpperCase(),
      status: this.modalIsActive ? 'Active' : 'Deactive'
    };
    this.expenseService.createExpenseCategory(payload).subscribe({
      next: () => {
        this.modalLoader = false;
        this.offcanvasRef.close();
        this.page = 0;
        this.endOfResult = false;
        this.expanseList = [];
        this.getCategories();
      },
      error: (err: any) => {
        this.modalLoader = false;
        this.modalErrorMessage = err?.error?.error?.message || 'Failed to add category';
      }
    });
  }

  updateCategory() {
    const payload = {
      name: this.modalCategoryName.trim().toUpperCase(),
      status: this.modalIsActive ? 'Active' : 'Deactive',
    };
    this.expenseService.updateExpenseCategory(this.editingId, payload).subscribe({
      next: () => {
        this.modalLoader = false;
        this.offcanvasRef.close();
        this.page = 0;
        this.endOfResult = false;
        this.expanseList = [];
        this.getCategories();
      },
      error: (err: any) => {
        this.modalLoader = false;
        this.modalErrorMessage = err?.error?.error?.message || 'Failed to update category';
      }
    });
  }

  closePanel() {
    this.offcanvasRef.close();
  }
}
